import React from "react";

/**
 * MinimalLoader – Clean gradient spinner
 * Pure CSS animations with rotating gradient box-shadows
 */

type LoaderProps = {
  /** visual scale multiplier */
  size?: number;
  /** animation speed in seconds; 2 = default */
  speed?: number;
  /** optional className to wrap container */
  className?: string;
  /** aria-label for screen readers */
  label?: string;
};

// Custom CSS properties interface for loader
interface LoaderCSSProperties extends React.CSSProperties {
  '--loader-size'?: string;
  '--loader-speed'?: string;
}

export function MinimalLoader({
  size = 1,
  speed = 2,
  className = "",
  label = "Lädt…",
}: LoaderProps) {
  const style: LoaderCSSProperties = {
    '--loader-size': `${180 * size}px`,
    '--loader-speed': `${speed}s`,
  };

  return (
    <div
      className={`loader-wrapper ${className}`}
      style={style}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className="loader"></div>

      {/* Component-scoped styles */}
      <style>{`
        .loader-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: var(--loader-size);
          height: var(--loader-size);
          font-family: "Inter", sans-serif;
          border-radius: 50%;
          background-color: transparent;
          user-select: none;
        }

        .loader {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 50%;
          background-color: transparent;
          animation: loader-rotate var(--loader-speed) linear infinite;
          z-index: 0;
        }

        @keyframes loader-rotate {
          0% {
            transform: rotate(90deg);
            box-shadow:
              0 10px 20px 0 #fff inset,
              0 20px 30px 0 #ad5fff inset,
              0 60px 60px 0 #471eec inset;
          }
          50% {
            transform: rotate(270deg);
            box-shadow:
              0 10px 20px 0 #fff inset,
              0 20px 10px 0 #d60a47 inset,
              0 40px 60px 0 #311e80 inset;
          }
          100% {
            transform: rotate(450deg);
            box-shadow:
              0 10px 20px 0 #fff inset,
              0 20px 30px 0 #ad5fff inset,
              0 60px 60px 0 #471eec inset;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .loader {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
